import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Typography } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Storage as ServerIcon,
  Security as ShieldIcon,
  Warning as WarningIcon,
  Medication as PillIcon,
  AccountBalance as GovernmentIcon,
  PhoneAndroid as MobileIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowRightIcon,
  Business as BuildingIcon,
  Wifi as WifiIcon,
  MonitorHeart as HeartbeatIcon,
  Phone as PhoneIcon,
  Notifications as BellIcon,
  Lock as LockIcon,
  Group as UsersIcon,
  Smartphone as SmartphoneIcon,
  LocationOn as MapPinIcon,
  Videocam as CameraIcon,
  ConfirmationNumber as TicketIcon,
  Settings as SettingsIcon,
  Monitor as MonitorIcon,
  Bolt as ZapIcon,
  Public as GlobeIcon,
  Favorite as HeartIcon,
  PlayArrow as PlayIcon,
  SettingsInputAntenna as RadioIcon,
  VpnKey as KeyIcon,
  Description as FileTextIcon,
  Dashboard as LayoutIcon,
  TrackChanges as TargetIcon,
  RocketLaunch as RocketIcon,
  Home as HomeIcon,
  Work as BriefcaseIcon,
  Apartment as BuildingAltIcon,
  Speed as SpeedIcon,
  SupportAgent as SupportIcon,
  Timer as TimerIcon,
  TrendingUp as TrendingUpIcon,
  CloudDone as CloudIcon,
  Elderly as ElderlyIcon,
  HealthAndSafety as HealthIcon,
  NightShelter as ShelterIcon,
  AccessTime as ClockIcon,
  LocalHospital as HospitalIcon,
  ContactPhone as ContactIcon,
  DoorFront as DoorIcon,
  Sensors as SensorIcon,
  Hub as HubIcon,
  IntegrationInstructions as IntegrationIcon,
  Euro as EuroIcon,
  Star as StarIcon,
  Email as EmailIcon,
  Language as WebIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import usePermission from '../../hooks/usePermission';

// CARO Brand Colors
const colors = {
  plum: {
    lighter: '#E7D6E0',
    light: '#C87DA1',
    main: '#9A3F69',
    dark: '#702C4D',
    darker: '#4A1C33',
  },
  gold: {
    lighter: '#F6EDD0',
    light: '#E7C87B',
    main: '#DAA03D',
    dark: '#B5842F',
    darker: '#78561F',
  }
};

// Common styles
const styles = {
  section: {
    position: 'absolute',
    inset: 0,
    transition: 'all 0.7s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heading: {
    fontSize: '3rem',
    fontWeight: 700,
    color: 'white',
    marginBottom: '0.5rem',
    textAlign: 'center',
  },
  subheading: {
    fontSize: '1.25rem',
    color: '#9ca3af',
    marginBottom: '3rem',
    textAlign: 'center',
  },
};

// Section Navigation Component
const SectionNav = ({ currentSection, totalSections, onNavigate }) => (
  <Box
    sx={{
      position: 'fixed',
      bottom: 32,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      zIndex: 50,
    }}
  >
    <IconButton
      onClick={() => onNavigate(currentSection - 1)}
      disabled={currentSection === 0}
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(8px)',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
        '&:disabled': { opacity: 0.3 },
        color: 'white',
      }}
    >
      <ChevronLeftIcon />
    </IconButton>

    <Box sx={{ display: 'flex', gap: 1 }}>
      {Array.from({ length: totalSections }).map((_, i) => (
        <Box
          key={i}
          onClick={() => onNavigate(i)}
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: i === currentSection ? 'white' : 'rgba(255,255,255,0.4)',
            transform: i === currentSection ? 'scale(1.25)' : 'scale(1)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': { bgcolor: i === currentSection ? 'white' : 'rgba(255,255,255,0.6)' },
          }}
        />
      ))}
    </Box>

    <IconButton
      onClick={() => onNavigate(currentSection + 1)}
      disabled={currentSection === totalSections - 1}
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(8px)',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
        '&:disabled': { opacity: 0.3 },
        color: 'white',
      }}
    >
      <ChevronRightIcon />
    </IconButton>
  </Box>
);

// Animated Background Component
const AnimatedBackground = ({ variant = 'default' }) => {
  const getGradient = () => {
    switch (variant) {
      case 'hero':
        return `linear-gradient(135deg, ${colors.plum.darker} 0%, ${colors.plum.dark} 40%, #111827 100%)`;
      case 'problem':
        return 'linear-gradient(135deg, #7f1d1d 0%, #111827 50%, #030712 100%)';
      case 'solution':
        return `linear-gradient(135deg, ${colors.plum.darker} 0%, ${colors.plum.main} 30%, #111827 100%)`;
      case 'gold':
        return `linear-gradient(135deg, ${colors.gold.darker} 0%, #111827 50%, #030712 100%)`;
      case 'success':
        return 'linear-gradient(135deg, #064e3b 0%, #111827 50%, #030712 100%)';
      default:
        return 'linear-gradient(135deg, #111827 0%, #030712 50%, #000 100%)';
    }
  };

  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', inset: 0, background: getGradient() }} />
      <Box
        sx={{
          position: 'absolute',
          top: '25%',
          left: '25%',
          width: 384,
          height: 384,
          borderRadius: '50%',
          opacity: 0.2,
          filter: 'blur(48px)',
          background: colors.plum.main,
          animation: 'pulse 4s ease-in-out infinite',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '25%',
          right: '25%',
          width: 320,
          height: 320,
          borderRadius: '50%',
          opacity: 0.15,
          filter: 'blur(48px)',
          background: colors.gold.main,
          animation: 'pulse 4s ease-in-out infinite',
          animationDelay: '1s',
        }}
      />
    </Box>
  );
};

// Section 1: Hero
const HeroSection = ({ isActive }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (isActive) {
      const timers = [
        setTimeout(() => setPhase(1), 300),
        setTimeout(() => setPhase(2), 800),
        setTimeout(() => setPhase(3), 1300),
      ];
      return () => timers.forEach(t => clearTimeout(t));
    }
    setPhase(0);
  }, [isActive]);

  return (
    <Box sx={{ ...styles.section, opacity: isActive ? 1 : 0, pointerEvents: isActive ? 'auto' : 'none' }}>
      <AnimatedBackground variant="hero" />

      <Box sx={{ position: 'relative', zIndex: 10, textAlign: 'center', px: 6 }}>
        {/* Logo */}
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: 4,
            bgcolor: colors.plum.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 4,
            opacity: phase >= 1 ? 1 : 0,
            transform: phase >= 1 ? 'scale(1)' : 'scale(0.5)',
            transition: 'all 0.6s ease',
            boxShadow: `0 0 60px ${colors.plum.main}50`,
          }}
        >
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '3rem' }}>C</Typography>
        </Box>

        <Typography
          sx={{
            fontSize: '4.5rem',
            fontWeight: 700,
            color: 'white',
            mb: 2,
            opacity: phase >= 2 ? 1 : 0,
            transform: phase >= 2 ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease',
          }}
        >
          CARO <span style={{ color: colors.gold.main }}>Care</span>
        </Typography>

        <Typography
          sx={{
            fontSize: '1.75rem',
            color: colors.plum.light,
            mb: 4,
            opacity: phase >= 2 ? 1 : 0,
            transform: phase >= 2 ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease',
            transitionDelay: '100ms',
          }}
        >
          Technologie voor Zorgzame Omgevingen
        </Typography>

        <Typography
          sx={{
            fontSize: '1.25rem',
            color: '#9ca3af',
            maxWidth: 700,
            mx: 'auto',
            lineHeight: 1.8,
            opacity: phase >= 3 ? 1 : 0,
            transform: phase >= 3 ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease',
          }}
        >
          Het complete platform voor assistentiewoningen, serviceflats en zorginstellingen.
          Van alarmbeheer tot toegangscontrole - alles in één geïntegreerde oplossing.
        </Typography>

        {/* Quick stats */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
            mt: 6,
            opacity: phase >= 3 ? 1 : 0,
            transition: 'all 0.6s ease',
            transitionDelay: '200ms',
          }}
        >
          {[
            { value: '10+', label: 'Jaar ervaring' },
            { value: '12', label: 'Projecten' },
            { value: '24/7', label: 'Ondersteuning' },
          ].map((stat) => (
            <Box key={stat.label} sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: colors.gold.main }}>{stat.value}</Typography>
              <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>{stat.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Section 2: The Problem
const ProblemSection = ({ isActive }) => {
  const [showItems, setShowItems] = useState(false);

  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => setShowItems(true), 500);
      return () => clearTimeout(timer);
    }
    setShowItems(false);
  }, [isActive]);

  const problems = [
    { icon: WarningIcon, title: 'Versnipperde systemen', desc: 'Alarmen, toegang, communicatie - alles apart' },
    { icon: TimerIcon, title: 'Trage responstijden', desc: 'Kritieke alarmen bereiken zorgverleners te laat' },
    { icon: SettingsIcon, title: 'Complex beheer', desc: 'Meerdere interfaces, geen centraal overzicht' },
    { icon: LockIcon, title: 'Beveiligingsrisico\'s', desc: 'Verouderde systemen, kwetsbare infrastructuur' },
    { icon: EuroIcon, title: 'Hoge operationele kosten', desc: 'Inefficiënte workflows, dubbel werk' },
    { icon: UsersIcon, title: 'Focus van het personeel', desc: 'Zorgmedewerkers besteden tijd aan techniek' },
  ];

  return (
    <Box sx={{ ...styles.section, opacity: isActive ? 1 : 0, pointerEvents: isActive ? 'auto' : 'none' }}>
      <AnimatedBackground variant="problem" />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1200, px: 6 }}>
        <Typography sx={{ ...styles.heading }}>
          De <span style={{ color: '#f87171' }}>Uitdaging</span>
        </Typography>
        <Typography sx={{ ...styles.subheading }}>
          Zorginstellingen worstelen dagelijks met verouderde technologie
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 3,
            maxWidth: 1000,
            mx: 'auto',
          }}
        >
          {problems.map((problem, i) => {
            const Icon = problem.icon;
            return (
              <Box
                key={problem.title}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'rgba(127, 29, 29, 0.3)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  opacity: showItems ? 1 : 0,
                  transform: showItems ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.5s ease',
                  transitionDelay: `${i * 100}ms`,
                }}
              >
                <Icon sx={{ width: 32, height: 32, color: '#f87171', mb: 2 }} />
                <Typography sx={{ color: 'white', fontWeight: 600, mb: 1 }}>{problem.title}</Typography>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.875rem' }}>{problem.desc}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// Section 3: The Solution Overview
const SolutionSection = ({ isActive }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (isActive) {
      const timers = [
        setTimeout(() => setPhase(1), 400),
        setTimeout(() => setPhase(2), 1000),
        setTimeout(() => setPhase(3), 1600),
      ];
      return () => timers.forEach(t => clearTimeout(t));
    }
    setPhase(0);
  }, [isActive]);

  return (
    <Box sx={{ ...styles.section, opacity: isActive ? 1 : 0, pointerEvents: isActive ? 'auto' : 'none' }}>
      <AnimatedBackground variant="solution" />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1200, px: 6 }}>
        <Typography sx={{ ...styles.heading }}>
          De CARO <span style={{ color: colors.gold.main }}>Oplossing</span>
        </Typography>
        <Typography sx={{ ...styles.subheading }}>
          Eén platform. Alle functionaliteit. Naadloze integratie.
        </Typography>

        {/* Central Hub Visualization */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
          {/* Left Features */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
            {[
              { icon: BellIcon, label: 'Alarmbeheer' },
              { icon: DoorIcon, label: 'Toegangscontrole' },
              { icon: PhoneIcon, label: 'Communicatie' },
            ].map((item, i) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 3,
                  py: 2,
                  borderRadius: 2,
                  bgcolor: `${colors.plum.darker}80`,
                  border: `1px solid ${colors.plum.dark}`,
                  opacity: phase >= 1 ? 1 : 0,
                  transform: phase >= 1 ? 'translateX(0)' : 'translateX(-30px)',
                  transition: 'all 0.5s ease',
                  transitionDelay: `${i * 100}ms`,
                }}
              >
                <Typography sx={{ color: 'white', fontWeight: 500 }}>{item.label}</Typography>
                <item.icon sx={{ color: colors.plum.light }} />
              </Box>
            ))}
          </Box>

          {/* Center - CARO Platform */}
          <Box
            sx={{
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${colors.plum.main}, ${colors.gold.dark})`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 80px ${colors.plum.main}40`,
              opacity: phase >= 2 ? 1 : 0,
              transform: phase >= 2 ? 'scale(1)' : 'scale(0.5)',
              transition: 'all 0.6s ease',
            }}
          >
            <HubIcon sx={{ width: 48, height: 48, color: 'white', mb: 1 }} />
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.25rem' }}>CARO</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem' }}>Platform</Typography>
          </Box>

          {/* Right Features */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
            {[
              { icon: SensorIcon, label: 'IoT Sensoren' },
              { icon: CameraIcon, label: 'Video Monitoring' },
              { icon: LayoutIcon, label: 'Centraal Dashboard' },
            ].map((item, i) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 3,
                  py: 2,
                  borderRadius: 2,
                  bgcolor: `${colors.plum.darker}80`,
                  border: `1px solid ${colors.plum.dark}`,
                  opacity: phase >= 1 ? 1 : 0,
                  transform: phase >= 1 ? 'translateX(0)' : 'translateX(30px)',
                  transition: 'all 0.5s ease',
                  transitionDelay: `${i * 100}ms`,
                }}
              >
                <item.icon sx={{ color: colors.gold.light }} />
                <Typography sx={{ color: 'white', fontWeight: 500 }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Bottom Benefits */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 4,
            mt: 6,
            opacity: phase >= 3 ? 1 : 0,
            transition: 'all 0.5s ease',
          }}
        >
          {[
            { icon: CloudIcon, label: 'Cloud-gebaseerd' },
            { icon: ShieldIcon, label: 'Beveiligd' },
            { icon: SpeedIcon, label: 'Real-time' },
            { icon: IntegrationIcon, label: 'Geïntegreerd' },
          ].map((item) => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#9ca3af' }}>
              <item.icon sx={{ width: 20, height: 20, color: colors.gold.main }} />
              <Typography sx={{ fontSize: '0.875rem' }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Section 4: Products
const ProductsSection = ({ isActive }) => {
  const [activeProduct, setActiveProduct] = useState(null);

  const products = [
    {
      id: 'buddy',
      name: 'CARO Buddy',
      Icon: HomeIcon,
      target: 'Particulier & Thuiszorg',
      price: ' ',
      desc: 'Ideaal voor senioren die thuis willen blijven wonen met extra veiligheid.',
      features: ['Persoonlijk alarmknop', 'Mantelzorg app', '24/7 noodcentrale'],
      color: colors.gold.main,
    },
    {
      id: 'pas',
      name: 'CARO Integrated PAS',
      Icon: BriefcaseIcon,
      target: 'Georganiseerde woonvormen',
      price: ' ',
      desc: 'Modulaire bouwblokken voor partners en projectontwikkelaars.',
      features: ['API integraties', 'White-label opties', 'Hardware bundels', 'Partner support'],
      color: colors.plum.light,
      popular: true,
    },
    {
      id: 'os',
      name: 'CARO OS',
      Icon: BuildingAltIcon,
      target: 'Uitbaters & Zorginstellingen',
      price: ' ',
      desc: 'Volledig platform voor grote assistentiewoning-complexen.',
      features: ['Onbeperkte units', 'Dedicated support', 'Custom integraties', 'SLA garantie'],
      color: colors.plum.main,
    },
  ];

  return (
    <Box sx={{ ...styles.section, opacity: isActive ? 1 : 0, pointerEvents: isActive ? 'auto' : 'none' }}>
      <AnimatedBackground variant="gold" />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1200, px: 6 }}>
        <Typography sx={{ ...styles.heading }}>
          Onze <span style={{ color: colors.gold.main }}>Producten</span>
        </Typography>
        <Typography sx={{ ...styles.subheading }}>
          Een oplossing voor elke schaal
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
          {products.map((product, i) => {
            const { Icon } = product;
            return (
              <Box
                key={product.id}
                sx={{
                  width: 320,
                  p: 4,
                  borderRadius: 3,
                  border: `2px solid ${activeProduct === product.id ? product.color : colors.plum.dark}`,
                  background: `linear-gradient(135deg, ${colors.plum.darker}90, ${colors.plum.dark}50)`,
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? 'translateY(0)' : 'translateY(32px)',
                  transition: 'all 0.5s ease',
                  transitionDelay: `${i * 150}ms`,
                  cursor: 'pointer',
                  position: 'relative',
                  '&:hover': { transform: 'translateY(-8px)', borderColor: product.color },
                }}
                onMouseEnter={() => setActiveProduct(product.id)}
                onMouseLeave={() => setActiveProduct(null)}
              >
                {product.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      right: 20,
                      px: 2,
                      py: 0.5,
                      borderRadius: 9999,
                      bgcolor: colors.gold.main,
                      color: 'black',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    12 Projecten
                  </Box>
                )}

                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: `${product.color}30`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <Icon sx={{ width: 32, height: 32, color: product.color }} />
                </Box>

                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'white', mb: 0.5 }}>
                  {product.name}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: product.color, mb: 1 }}>
                  {product.target}
                </Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: colors.gold.main, mb: 2 }}>
                  {product.price}
                </Typography>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.875rem', mb: 3, minHeight: 48 }}>
                  {product.desc}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {product.features.map((feature) => (
                    <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ width: 16, height: 16, color: '#4ade80' }} />
                      <Typography sx={{ color: '#d1d5db', fontSize: '0.875rem' }}>{feature}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// Section 5: Features
const FeaturesSection = ({ isActive }) => {
  const features = [
    { Icon: BellIcon, name: 'Smart Alarmen', desc: 'Intelligente alarmverwerking met prioritering' },
    { Icon: PhoneIcon, name: 'Videofonie', desc: 'Videocommunicatie aan de deur' },
    { Icon: DoorIcon, name: 'Toegangscontrole', desc: 'Digitale sloten en badges' },
    { Icon: SensorIcon, name: 'IoT Sensoren', desc: 'Draadloze knoppen en sensoren' },
    { Icon: MapPinIcon, name: 'Indoor Navigatie', desc: 'Locatiebepaling in het gebouw' },
    { Icon: CameraIcon, name: 'GDPR Camera\'s', desc: 'Privacy-compliant video aanvragen' },
    { Icon: TicketIcon, name: 'Ticketing', desc: 'Geïntegreerd issue management' },
    { Icon: UsersIcon, name: 'Gebruikersbeheer', desc: 'Rollen en rechten beheer' },
  ];

  return (
    <Box sx={{ ...styles.section, opacity: isActive ? 1 : 0, pointerEvents: isActive ? 'auto' : 'none' }}>
      <AnimatedBackground />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1200, px: 6 }}>
        <Typography sx={{ ...styles.heading }}>
          Krachtige <span style={{ color: colors.plum.light }}>Features</span>
        </Typography>
        <Typography sx={{ ...styles.subheading }}>
          Alles wat u nodig heeft in één platform
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 3,
            maxWidth: 1000,
            mx: 'auto',
          }}
        >
          {features.map((feature, i) => {
            const { Icon } = feature;
            return (
              <Box
                key={feature.name}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: `${colors.plum.darker}60`,
                  border: `1px solid ${colors.plum.dark}`,
                  textAlign: 'center',
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.5s ease',
                  transitionDelay: `${i * 75}ms`,
                  '&:hover': {
                    bgcolor: `${colors.plum.dark}60`,
                    borderColor: colors.plum.light,
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Icon sx={{ width: 40, height: 40, color: colors.gold.main, mb: 2 }} />
                <Typography sx={{ color: 'white', fontWeight: 600, mb: 1 }}>{feature.name}</Typography>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>{feature.desc}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// Section 6: One Stop Shopping - Integrations
const IntegrationsSection = ({ isActive }) => {
  const [showItems, setShowItems] = useState(false);

  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => setShowItems(true), 400);
      return () => clearTimeout(timer);
    }
    setShowItems(false);
  }, [isActive]);

  const integrations = [
    // Ready integrations
    { name: 'Apparaatbeheer (MDM)', desc: 'Centraal beheer van alle tablets en andere endpoints', status: 'ready', Icon: MonitorIcon },
    { name: 'VoIP Telefonie', desc: 'Telefooncentrale integratie per locatie met SIP-trunks naar externe providers', status: 'ready', Icon: PhoneIcon },
    { name: 'Videofonie & Intercom', desc: 'IP intercom en toegangscontrole', status: 'ready', Icon: CameraIcon },
    { name: 'Noodoproep Sturing', desc: 'Alarmroutering naar externe partners of mantelzorgers', status: 'ready', Icon: BellIcon },
    { name: 'Netwerk Beheer', desc: 'Firewall, Switches, AP\'s en gateways', status: 'ready', Icon: WifiIcon },
    { name: 'IoT Sensoren', desc: 'Draadloze en batterijloze knoppen en sensoren', status: 'ready', Icon: SensorIcon },
    // Coming soon
    { name: 'Domotica', desc: 'Smart home automatisering', status: 'ready', Icon: LightbulbIcon },
    { name: 'Beveiligde Opslag', desc: 'Documenten en dossiers', status: 'soon', Icon: ShieldIcon },
    { name: 'CRM & Financieel', desc: 'Boekhouding integratie', status: 'soon', Icon: EuroIcon },
    { name: 'Plaatsbeschrijvingen', desc: 'Inspectie en waarborg beheer', status: 'soon', Icon: ShelterIcon },
    { name: 'Personeelsplanning', desc: 'Roosters en bezetting', status: 'soon', Icon: UsersIcon },
    { name: 'Valdetectie', desc: 'Automatische valalarmering', status: 'soon', Icon: HeartbeatIcon },
    { name: 'Medicatiebeheer', desc: 'Herinneringen en opvolging', status: 'soon', Icon: PillIcon },
    { name: 'Integratie met zorgplatforms van FOD', desc: 'Naadloze koppeling met overheidsdiensten', status: 'soon', Icon: GovernmentIcon },
    { name: 'Dwaaldetectie', desc: 'Locatiebepaling en waarschuwingen', status: 'soon', Icon: MapPinIcon },
    { name: 'Alarm anywhere', desc: 'Mobiele alarmering zoals je gewend bent, maar ook buiten de muren van het gebouw', status: 'soon', Icon: MobileIcon },
    { name: 'En nog zo veel meer', desc: 'We luisteren naar jullie noden...', status: 'soon', Icon: IntegrationIcon },
  ];

  const services = [
    { name: 'api.caro.care', Icon: GlobeIcon, desc: 'Dynamic REST API' },
    { name: 'mqtt.caro.care', Icon: RadioIcon, desc: 'IoT Protocol' },
    { name: 'portal.caro.care', Icon: LayoutIcon, desc: 'Web Dashboard' },
    { name: 'network.caro.care', Icon: WifiIcon, desc: 'Netwerk Beheer' },
    { name: 'files.caro.care', Icon: ShieldIcon, desc: 'Veilige opslag' },
    { name: 'ac.caro.care', Icon: KeyIcon, desc: 'Videofonie' },
    { name: 'sv.caro.care', Icon: PhoneIcon, desc: 'Toegangscontrole' },
  ];

  return (
    <Box sx={{ ...styles.section, opacity: isActive ? 1 : 0, pointerEvents: isActive ? 'auto' : 'none' }}>
      <AnimatedBackground variant="solution" />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1400, px: 4 }}>
        <Typography sx={{ ...styles.heading, mb: 1 }}>
          One Stop <span style={{ color: colors.gold.main }}>Shopping</span>
        </Typography>
        <Typography sx={{ ...styles.subheading, mb: 4 }}>
          Alle integraties die u nodig heeft - van apparaatbeheer tot alarmbeheer
        </Typography>

        {/* Integration Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 2,
            maxWidth: 1200,
            mx: 'auto',
            mb: 4,
          }}
        >
          {integrations.map((integration, i) => {
            const { Icon } = integration;
            const isReady = integration.status === 'ready';
            return (
              <Box
                key={integration.name}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: isReady ? `${colors.plum.darker}80` : 'rgba(75, 85, 99, 0.3)',
                  border: `1px solid ${isReady ? colors.plum.dark : 'rgba(75, 85, 99, 0.5)'}`,
                  textAlign: 'center',
                  opacity: showItems ? 1 : 0,
                  transform: showItems ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.4s ease',
                  transitionDelay: `${i * 50}ms`,
                  position: 'relative',
                }}
              >
                {/* Status badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    px: 1,
                    py: 0.25,
                    borderRadius: 9999,
                    bgcolor: isReady ? 'rgba(34, 197, 94, 0.2)' : 'rgba(234, 179, 8, 0.2)',
                    border: `1px solid ${isReady ? 'rgba(34, 197, 94, 0.5)' : 'rgba(234, 179, 8, 0.5)'}`,
                  }}
                >
                  <Typography sx={{ fontSize: '0.6rem', color: isReady ? '#4ade80' : '#facc15', fontWeight: 600 }}>
                    {isReady ? 'READY' : 'SOON'}
                  </Typography>
                </Box>
                <Icon sx={{ width: 28, height: 28, color: isReady ? colors.gold.main : '#6b7280', mb: 1 }} />
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>
                  {integration.name}
                </Typography>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>{integration.desc}</Typography>
              </Box>
            );
          })}
        </Box>

        {/* Services */}
        <Box sx={{ display: 'none', justifyContent: 'center', gap: 2, mb: 4 }}>
          {services.map((service, i) => {
            const { Icon } = service;
            return (
              <Box
                key={service.name}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: `${colors.plum.darker}60`,
                  border: `1px solid ${colors.plum.dark}`,
                  textAlign: 'center',
                  opacity: showItems ? 1 : 0,
                  transition: 'all 0.5s ease',
                  transitionDelay: `${600 + i * 100}ms`,
                }}
              >
                <Icon sx={{ width: 20, height: 20, color: colors.gold.main, mb: 0.5 }} />
                <Typography sx={{ fontFamily: 'monospace', color: 'white', fontSize: '0.75rem' }}>
                  {service.name}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Custom & Whitelabel */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            opacity: showItems ? 1 : 0,
            transition: 'all 0.5s ease',
            transitionDelay: '800ms',
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${colors.gold.darker}40, ${colors.gold.dark}20)`,
              border: `1px solid ${colors.gold.dark}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <IntegrationIcon sx={{ width: 24, height: 24, color: colors.gold.main }} />
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>
                Custom Integraties
              </Typography>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                Uw bestaande systemen aansluiten op maat
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${colors.plum.darker}40, ${colors.plum.dark}20)`,
              border: `1px solid ${colors.plum.dark}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <StarIcon sx={{ width: 24, height: 24, color: colors.plum.light }} />
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>
                Whitelabeling Mogelijk
              </Typography>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                Volledig onder uw eigen merk en huisstijl
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Section 7: Benefits / Why CARO
const BenefitsSection = ({ isActive }) => {
  const benefits = [
    {
      Icon: SpeedIcon,
      title: 'Snellere responstijden',
      stat: '60%',
      desc: 'Snellere alarmafhandeling door slimme routing',
    },
    {
      Icon: EuroIcon,
      title: 'Lagere kosten',
      stat: '40%',
      desc: 'Besparing op operationele kosten',
    },
    {
      Icon: TrendingUpIcon,
      title: 'Hogere tevredenheid',
      stat: '95%',
      desc: 'Bewonerstevredenheid met het systeem',
    },
    {
      Icon: TimerIcon,
      title: 'Minder administratie',
      stat: '50%',
      desc: 'Tijdsbesparing door automatisering',
    },
  ];

  return (
    <Box sx={{ ...styles.section, opacity: isActive ? 1 : 0, pointerEvents: isActive ? 'auto' : 'none' }}>
      <AnimatedBackground variant="success" />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1200, px: 6 }}>
        <Typography sx={{ ...styles.heading }}>
          Waarom <span style={{ color: colors.gold.main }}>CARO</span>?
        </Typography>
        <Typography sx={{ ...styles.subheading }}>
          Meetbare resultaten voor uw organisatie
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
          {benefits.map((benefit, i) => {
            const { Icon } = benefit;
            return (
              <Box
                key={benefit.title}
                sx={{
                  width: 240,
                  p: 4,
                  borderRadius: 3,
                  bgcolor: 'rgba(6, 78, 59, 0.3)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  textAlign: 'center',
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'all 0.5s ease',
                  transitionDelay: `${i * 150}ms`,
                }}
              >
                <Icon sx={{ width: 40, height: 40, color: '#4ade80', mb: 2 }} />
                <Typography sx={{ fontSize: '3rem', fontWeight: 700, color: colors.gold.main, lineHeight: 1 }}>
                  {benefit.stat}
                </Typography>
                <Typography sx={{ color: 'white', fontWeight: 600, mt: 1, mb: 1 }}>{benefit.title}</Typography>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>{benefit.desc}</Typography>
              </Box>
            );
          })}
        </Box>

        {/* Testimonial */}
        <Box
          sx={{
            mt: 6,
            p: 4,
            borderRadius: 3,
            bgcolor: `${colors.plum.darker}50`,
            border: `1px solid ${colors.plum.dark}`,
            maxWidth: 700,
            mx: 'auto',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: 'white', fontSize: '1.125rem', fontStyle: 'italic', mb: 2 }}>
            "CARO heeft onze dagelijkse operatie volledig getransformeerd. Wat vroeger uren kostte, doen we nu in minuten."
          </Typography>
          <Typography sx={{ color: colors.gold.main, fontWeight: 500 }}>
            - Facilitymanager, Assistentiewoning Vlaanderen
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Section 8: Call to Action
const CTASection = ({ isActive }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (isActive) {
      const timers = [
        setTimeout(() => setPhase(1), 300),
        setTimeout(() => setPhase(2), 800),
        setTimeout(() => setPhase(3), 1300),
      ];
      return () => timers.forEach(t => clearTimeout(t));
    }
    setPhase(0);
  }, [isActive]);

  return (
    <Box sx={{ ...styles.section, opacity: isActive ? 1 : 0, pointerEvents: isActive ? 'auto' : 'none' }}>
      <AnimatedBackground variant="hero" />

      <Box sx={{ position: 'relative', zIndex: 10, textAlign: 'center', px: 6 }}>
        <RocketIcon
          sx={{
            width: 80,
            height: 80,
            color: colors.gold.main,
            mb: 3,
            opacity: phase >= 1 ? 1 : 0,
            transform: phase >= 1 ? 'translateY(0)' : 'translateY(-20px)',
            transition: 'all 0.6s ease',
          }}
        />

        <Typography
          sx={{
            fontSize: '3.5rem',
            fontWeight: 700,
            color: 'white',
            mb: 2,
            opacity: phase >= 1 ? 1 : 0,
            transition: 'all 0.6s ease',
          }}
        >
          Klaar om te <span style={{ color: colors.gold.main }}>starten</span>?
        </Typography>

        <Typography
          sx={{
            fontSize: '1.25rem',
            color: '#9ca3af',
            maxWidth: 600,
            mx: 'auto',
            mb: 6,
            opacity: phase >= 2 ? 1 : 0,
            transition: 'all 0.6s ease',
          }}
        >
          Ontdek hoe CARO uw zorginstelling kan transformeren met een vrijblijvende demo.
        </Typography>

        {/* CTA Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            mb: 6,
            opacity: phase >= 2 ? 1 : 0,
            transition: 'all 0.6s ease',
          }}
        >
          <Box
            sx={{
              px: 6,
              py: 2,
              borderRadius: 2,
              bgcolor: colors.gold.main,
              color: 'black',
              fontWeight: 600,
              fontSize: '1.125rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '&:hover': { bgcolor: colors.gold.light },
            }}
          >
            <PlayIcon />
            Demo Aanvragen
          </Box>
          <Box
            sx={{
              px: 6,
              py: 2,
              borderRadius: 2,
              border: `2px solid ${colors.plum.light}`,
              color: 'white',
              fontWeight: 600,
              fontSize: '1.125rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '&:hover': { bgcolor: `${colors.plum.main}30` },
            }}
          >
            <ContactIcon />
            Contact Opnemen
          </Box>
        </Box>

        {/* Contact Info */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
            opacity: phase >= 3 ? 1 : 0,
            transition: 'all 0.6s ease',
          }}
        >
          {[
            { Icon: WebIcon, label: 'www.caro.care' },
            { Icon: EmailIcon, label: 'info@caro.care' },
            { Icon: PhoneIcon, label: '+32 (0)9 123 45 67' },
          ].map((item) => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#9ca3af' }}>
              <item.Icon sx={{ width: 20, height: 20, color: colors.plum.light }} />
              <Typography>{item.label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 8, opacity: phase >= 3 ? 1 : 0, transition: 'all 0.6s ease' }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              bgcolor: colors.plum.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.5rem' }}>C</Typography>
          </Box>
          <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
            CARO Care - Technologie voor Zorgzame Omgevingen
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Main Component
const SalesPresentation = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [currentSection, setCurrentSection] = useState(0);

  const isAdmin = hasPermission('API-management-portal', 'users.permissions.manage');

  const sections = [
    HeroSection,
    ProblemSection,
    SolutionSection,
    ProductsSection,
    FeaturesSection,
    IntegrationsSection,
    BenefitsSection,
    CTASection,
  ];

  const sectionTitles = [
    'CARO Care',
    'De Uitdaging',
    'De Oplossing',
    'Producten',
    'Features',
    'Integraties',
    'Voordelen',
    'Contact',
  ];

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        setCurrentSection((prev) => Math.min(prev + 1, sections.length - 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentSection((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Escape') {
        navigate(-1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, sections.length]);

  // Permission check
  if (!isAdmin) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#111827',
        }}
      >
        <Typography sx={{ color: '#9ca3af' }}>Je hebt geen toegang tot deze pagina.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        bgcolor: 'black',
        fontFamily: 'Inter, system-ui, sans-serif',
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 },
        },
      }}
    >
      {/* Sections */}
      {sections.map((Section, i) => (
        <Section key={i} isActive={currentSection === i} />
      ))}

      {/* Navigation */}
      <SectionNav currentSection={currentSection} totalSections={sections.length} onNavigate={setCurrentSection} />

      {/* Keyboard hint */}
      <Box sx={{ position: 'absolute', bottom: 32, right: 32, color: '#4b5563', fontSize: '0.75rem' }}>
        ← → of spatiebalk | ESC om af te sluiten
      </Box>
    </Box>
  );
};

export default SalesPresentation;
